import { z } from "zod";

/**
 * Manual review log (Phase 6) — the BrightLocal-20 tracker. Reviews are
 * logged by hand (platform, link, optional screenshot reference) rather
 * than pulled live from each platform's API in v1.
 *
 * `contact_id` and `event_id` are nullable: the "Log review" dialog (per
 * the plan) only ever asks for an *optional* event link and never asks for
 * a contact directly, so a review can be logged with no event at all
 * (contact_id presumably resolved server-side from the event's contact
 * when one is given, else left null — this file makes no assumption about
 * that resolution, only that the field can come back empty).
 */

export const ReviewPlatformSchema = z.enum([
  "google",
  "knot",
  "yelp",
  "instagram",
  "other",
]);
export type ReviewPlatform = z.infer<typeof ReviewPlatformSchema>;

export const REVIEW_PLATFORM_LABELS: Record<ReviewPlatform, string> = {
  google: "Google",
  knot: "The Knot",
  yelp: "Yelp",
  instagram: "Instagram",
  other: "Other",
};

export const REVIEW_PLATFORM_BADGE_VARIANT: Record<
  ReviewPlatform,
  "default" | "success" | "warning" | "danger" | "info" | "secondary" | "primary"
> = {
  google: "info",
  knot: "primary",
  yelp: "danger",
  instagram: "warning",
  other: "secondary",
};

export const ReviewSchema = z.object({
  id: z.string(),
  event_id: z.string().nullable(),
  contact_id: z.string().nullable(),
  platform: ReviewPlatformSchema,
  url: z.string(),
  rating: z.number().nullable(),
  screenshot_key: z.string().nullable(),
  received_at: z.string(),
  notes: z.string().nullable(),
  created_at: z.string(),
});
export type Review = z.infer<typeof ReviewSchema>;

export const ReviewListResponseSchema = z.object({
  items: z.array(ReviewSchema),
  next_cursor: z.string().nullable(),
});
export type ReviewListResponse = z.infer<typeof ReviewListResponseSchema>;

/**
 * The "N of 20" BrightLocal-20 counter (GET /v2/reviews/count). `target` is
 * server-authoritative (not hardcoded to 20 client-side) so the progress
 * bar and "N of {target}" copy both read from the same number the plan
 * calls out as the review-count-to-20 tracker's threshold.
 */
export const ReviewCountResponseSchema = z.object({
  total: z.number(),
  target: z.number(),
  remaining: z.number(),
});
export type ReviewCountResponse = z.infer<typeof ReviewCountResponseSchema>;

// ---- Mutation inputs -------------------------------------------------------

export interface CreateReviewInput {
  platform: ReviewPlatform;
  url: string;
  rating?: number | null;
  event_id?: string | null;
  received_at?: string;
  notes?: string | null;
}

export interface PatchReviewInput {
  id: string;
  platform?: ReviewPlatform;
  url?: string;
  rating?: number | null;
  event_id?: string | null;
  received_at?: string;
  notes?: string | null;
}
